import * as Sentry from "@sentry/react";
import { config } from "../Constants";
import { toCamel } from '../helper/reponseFormatter';

interface LogInfo {
  level: Sentry.SeverityLevel,
  message: string,
  timestamp: number,
  extra?: { [key: string]: any },
}

/**
 * Sanitize request options before they are attached to a Sentry event. When `sensitive` is set (used by
 * the credential endpoints), the request body carries a plaintext API key / broker number and MUST NOT be
 * shipped to telemetry; the Authorization bearer token is masked too. Returns a shallow copy — the real
 * `options` passed to fetch() is never mutated.
 */
const sanitizeOptionsForLog = (options: RequestInit, sensitive: boolean): RequestInit => {
  if (!sensitive) return options;
  const headers: Record<string, string> = { ...(options.headers as any) };
  if (headers.Authorization) headers.Authorization = '[redacted]';
  return { ...options, body: '[redacted-sensitive]', headers };
};

/**
 * Fetch wrapper. Resolves with the camel-cased JSON body, or with an `{ ok: false, ... }` shape on a
 * non-2xx, or with `undefined` when the request could not be made at all — it does not reject.
 *
 * ⚠️ ONE EXCEPTION: if you pass `options.signal` and abort it, this REJECTS with that `AbortError`.
 * A cancellation is not a failure and must not be reported as one — it is neither logged to Sentry nor
 * flattened into the `undefined` return, because callers read the result as `res?.data`, which would
 * make a request you deliberately cancelled indistinguishable from a successful empty one. Callers that
 * pass a signal therefore need a `.catch`; callers that do not can never see this and are unaffected.
 */
const apiClient = async (url: RequestInfo | URL, options: RequestInit = {}, logInfo?: Partial<LogInfo>, sensitive: boolean = false ) => {
  try {
    // Reset idle timer on API call (if not in warning state)
    if (typeof window !== 'undefined' && (window as any).__resetIdleTimer) {
      try {
        (window as any).__resetIdleTimer();
      } catch (err) {
        // Silently fail if timer reset fails
      }
    }
    
    const response = await fetch(config + url, options);
    if (!response.ok) {
      const res = {
        status: response.status,
        statusText: response.statusText,
        url: response.url,
      }
      const resp = await response.json().then(json => {
        const errors = JSON.stringify(json.errors)
        return { ok: false, ...res, ...json, errors }
      }).catch(err => {
        return { ok: false, ...res }
      })
      console.error("Fetch Error: ", { url, response: resp })
     //alert("Fetch Error: " + url)
      Sentry.captureMessage(`Fetch Error: ${url}`, {
        level: 'error',
        extra: {
          url,
          options: sanitizeOptionsForLog(options, sensitive),
          responseData: resp,
        }
      })
      return resp as any
    }
    if (logInfo) {
      Sentry.captureEvent({
        message: logInfo.message || '',
        level: logInfo.level || 'info',
        extra: logInfo.extra || {},
      })
    }
    try {
      const rawJson = await response.clone().json()
      return toCamel(rawJson) as any;
    } catch (err) {
      // response type is File (api: '/StateReports/UpdateBr18')
      return response
    }
  } catch (error: any) {
    // AN ABORT IS NOT A FAILURE — it is this app deciding the response is no longer wanted (a newer
    // filter scope superseded it, or the screen unmounted). Two things must not happen to it:
    //   * it must not be filed to Sentry, which would fill the error stream with work we chose to stop;
    //   * it must not fall through to the `return undefined` below, because callers read the result as
    //     `res?.data?.data` — an aborted request would then look exactly like a successful EMPTY one
    //     and could write an empty result set over real rows.
    // Rethrowing keeps it visible to the one caller that owns the AbortController and invisible to
    // every caller that passes no signal, since they can never see an abort in the first place.
    if (error?.name === 'AbortError' || options.signal?.aborted) throw error;
    //alert("Unexpected error during fetch: " + url)
    Sentry.captureMessage('Unexpected error during fetch: ', {
      level: 'error',
      extra: {
        url,
        options: sanitizeOptionsForLog(options, sensitive),
      }
    })
    return
    // throw error
  }
}

export {
  apiClient
}